import { useState } from "react";
import RichtextAdapter from "modules/storyblok/adapters/RichtextAdapter";
import { contentStyles } from "modules/storyblok/adapters/Content";
import { trackUAEvent, trackCustomEvent } from "utils/tracking";
import { Flex, Box, Image, SimpleGrid, useBreakpointValue } from "@chakra-ui/react";
import { CardFooter } from "components/TrustpilotReviewCard";
import type { TestimonialCardProps } from "./TestimonialCard.d";
import { CardDetail } from "./CardDetails";
import { PlayButton } from "./PlayButton";

const TESTIMONIAL_VIDEO_CLICK_EVENT = ["clicked", "TestimonialHP", "Video"] as const;
const HERO_BARCODE_SCANNER_EVENT = { event: "cta_click", label: "video", placement: "testimonial_hp" } as const;

export const TestimonialCard = ({
  reviewText,
  author,
  avatarSrc,
  imageSrc,
  videoSrc,
  alt,
  details,
  ...props
}: TestimonialCardProps) => {
  const [showVideo, setShowVideo] = useState(false);

  const clickHandler = () => {
    setShowVideo(true);
    trackUAEvent(TESTIMONIAL_VIDEO_CLICK_EVENT);
    trackCustomEvent(HERO_BARCODE_SCANNER_EVENT);
  };

  return (
    <Flex
      w={{ base: "100%", lg: "unset" }}
      p={{ base: 2, md: 4 }}
      background="greyscale.100"
      borderRadius={useBreakpointValue({ base: "lg", lg: "2xl" })}
      maxW="1000px"
      {...props}
      direction={{ base: "column", lg: "row" }}
    >
      <Flex
        cursor="pointer"
        w="100%"
        display="flex"
        justifyContent={{ base: "center", lg: "flex-end" }}
        height={{ base: 80, lg: "474px" }}
        borderRadius="lg"
        overflow="hidden"
        position="relative"
      >
        {showVideo ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video controls poster={imageSrc} width="100%" height="100%" autoPlay src={videoSrc} />
        ) : (
          <>
            <Image src={imageSrc} alt={alt} w="100%" borderRadius="lg" objectFit="cover" />
            <Flex
              position="absolute"
              mx="auto"
              w="100%"
              align="center"
              justify="center"
              height="100%"
              onClick={clickHandler}
            >
              <PlayButton width="112px" height="112px" />
            </Flex>
          </>
        )}
      </Flex>

      <Flex direction="column" px={{ base: 3, md: 6, lg: 8 }} justify="space-between" py={5}>
        <Box pb={6}>
          <RichtextAdapter content={reviewText} customStyles={{ ...contentStyles, paragraph: { fontSize: "md" } }} />
          {author && <CardFooter avatarSrc={avatarSrc} author={author} mt={5} />}
        </Box>
        <SimpleGrid columns={2} spacing={{ base: 4, lg: 8 }} width="fit-content">
          {details?.map((data) => (
            <CardDetail {...data} key={data.title} />
          ))}
        </SimpleGrid>
      </Flex>
    </Flex>
  );
};
